import type { NavItemGroup, NavItems } from '../types';
import { getIsPathActive } from '../variants/SideNavigation/helpers';

const SUPPORTED_NAV_ITEM_TYPES = ['GROUP', 'URL', 'APPLICATION_PAGE'];

export type GetIsNavGroupActiveProps = {
  item: NavItemGroup;
  activeMenuItemId: string | null | undefined;
  projectSlug?: string;
  activePathnames: string[];
};

/**
 * Returns true if any of SubItems of Navigation Group is active
 */
export const getIsNavGroupActive = (props: GetIsNavGroupActiveProps) => {
  const { activeMenuItemId, activePathnames, item, projectSlug } = props;

  if (activeMenuItemId && activeMenuItemId !== item.id) {
    return false;
  }

  const isActive = item.items.some((subItem) => {
    if (subItem.type !== 'GROUP') {
      return getIsPathActive({
        activePathnames,
        path: subItem.url,
      });
    } else if (subItem.type === 'GROUP') {
      return getIsNavGroupActive({
        activeMenuItemId,
        activePathnames,
        item: subItem,
        projectSlug,
      });
    }
    return false;
  });

  return isActive;
};

export function getEnabledGroups<T extends { items?: object[] }>(
  items: (T & { type: string; enabled?: boolean })[],
) {
  return items
    .filter((item) => {
      if (item.enabled === false) return false;

      // hide group if all items are disabled
      if (item.type === 'GROUP' && !item.items?.length) return false;

      return true;
    })
    .map(({ enabled, ...rest }) => rest) as T[];
}

/**
 * NavItem of type GROUP may have items of type GROUP,URL,ACTION,LABEL,CUSTOM......
 * But we support only rendering GROUP and URL item for GroupNavItem.
 * Therefore, remove all other items except GROUP and URL from the group.
 */
export const removeUnsupportedItemsFromGroup = (navItems: NavItems, isGroupItem?: boolean) => {
  return navItems
    .filter((item) => {
      if (isGroupItem && !SUPPORTED_NAV_ITEM_TYPES.includes(item.type)) {
        return false;
      }

      return true;
    })
    .map((item) => {
      if (item.type !== 'GROUP') {
        return item;
      }

      const filteredItems = item.items
        .filter((groupItem) => SUPPORTED_NAV_ITEM_TYPES.includes(groupItem.type))
        .map((groupItem) => {
          if (groupItem.type === 'GROUP') {
            return {
              ...groupItem,
              items: removeUnsupportedItemsFromGroup(groupItem.items, true),
            };
          }
          return groupItem;
        });

      return {
        ...item,
        items: filteredItems,
      };
    });
};
